import {
  STOCKER_DROP_LP_RELEASE_PROGRESS,
  STOCKER_PICKUP_OHT_TAKE_PROGRESS,
} from '../components/canvas/amhs/Stocker/stockerTransferTiming';
import type { AmhsLiveInventory, LiveOhtRuntimeFrame } from '../types/amhsStream';
import { liveStockerColumnSplit } from '../features/simulation/liveStockerInventory';
import { liveInventoryDisplayStore } from '../store/liveInventoryDisplayStore';
import { liveStreamInventoryStore } from '../store/liveStreamInventoryStore';
import { liveOhtRuntimeStore } from '../store/liveOhtRuntimeStore';
import { simulationFrameBus } from '../store/simulationFrameBus';

type Listener = () => void;

type TrackedTransfer = {
  action: 'PICKUP' | 'DROP';
  progress01: number;
};

const refreshListeners = new Set<Listener>();
let trackedTransfers = new Map<string, TrackedTransfer>();
let frameUnsub: (() => void) | null = null;
let inventoryUnsub: (() => void) | null = null;
let streamInventoryUnsub: (() => void) | null = null;
let lastFrameNotifyMs = 0;
let lastHeldInventoryFingerprint = '';

/** Coalesce schedule-phase refreshes — 3D StockerTransferAnim runs every frame. */
const FRAME_NOTIFY_MS = 50;

function stockerInventoryFingerprint(inventory: AmhsLiveInventory | null): string {
  if (!inventory?.stockers?.length) return '';
  return inventory.stockers
    .map((stocker) => {
      const columns = liveStockerColumnSplit(stocker);
      return `${stocker.stockerId}:${columns?.raw ?? ''}:${columns?.processed ?? ''}:${stocker.occupancy ?? ''}`;
    })
    .join('|');
}

function isPastMilestone(action: 'PICKUP' | 'DROP', progress01: number): boolean {
  const threshold =
    action === 'DROP' ? STOCKER_DROP_LP_RELEASE_PROGRESS : STOCKER_PICKUP_OHT_TAKE_PROGRESS;
  return progress01 + 1e-6 >= threshold;
}

function extractTransfers(frame: LiveOhtRuntimeFrame): Map<string, TrackedTransfer> {
  const next = new Map<string, TrackedTransfer>();
  for (const snapshot of Object.values(frame.byOhtId)) {
    const transfer = snapshot.transfer;
    if (!transfer) continue;
    next.set(transfer.eventId, {
      action: transfer.action,
      progress01: transfer.progress01,
    });
  }
  return next;
}

/** True when a transfer starts/ends or crosses an LP show/hide milestone. */
export function stocker2dDisplayRuntimeChanged(
  prev: ReadonlyMap<string, TrackedTransfer>,
  next: ReadonlyMap<string, TrackedTransfer>
): boolean {
  if (prev.size !== next.size) return true;

  for (const [eventId, nextTransfer] of next) {
    const prevTransfer = prev.get(eventId);
    if (!prevTransfer) return true;
    const prevPast = isPastMilestone(prevTransfer.action, prevTransfer.progress01);
    const nextPast = isPastMilestone(nextTransfer.action, nextTransfer.progress01);
    if (prevPast !== nextPast) return true;
  }

  for (const eventId of prev.keys()) {
    if (!next.has(eventId)) return true;
  }

  return false;
}

function notifyRefreshListeners(): void {
  for (const listener of refreshListeners) listener();
}

function onScheduleFrame(): void {
  const next = extractTransfers(liveOhtRuntimeStore.getFrame());
  const milestoneChanged = stocker2dDisplayRuntimeChanged(trackedTransfers, next);
  trackedTransfers = next;

  const now = performance.now();
  const elapsed = now - lastFrameNotifyMs;
  const hasActiveTransfer = next.size > 0;

  if (milestoneChanged) {
    lastFrameNotifyMs = now;
    notifyRefreshListeners();
    return;
  }

  if (!hasActiveTransfer) return;

  if (elapsed >= FRAME_NOTIFY_MS) {
    lastFrameNotifyMs = now;
    notifyRefreshListeners();
  }
}

function onHeldInventoryChange(): void {
  const next = stockerInventoryFingerprint(liveInventoryDisplayStore.getSnapshot());
  if (next === lastHeldInventoryFingerprint) return;
  lastHeldInventoryFingerprint = next;
  notifyRefreshListeners();
}

function onStreamInventoryChange(): void {
  notifyRefreshListeners();
}

function ensureFrameSubscription(): void {
  if (frameUnsub) return;
  trackedTransfers = extractTransfers(liveOhtRuntimeStore.getFrame());
  lastFrameNotifyMs = 0;
  frameUnsub = simulationFrameBus.subscribe(onScheduleFrame, 'schedule');
}

function ensureInventorySubscriptions(): void {
  if (!inventoryUnsub) {
    inventoryUnsub = liveInventoryDisplayStore.subscribe(onHeldInventoryChange);
  }
  if (!streamInventoryUnsub) {
    streamInventoryUnsub = liveStreamInventoryStore.subscribe(onStreamInventoryChange);
  }
}

/** Clear milestone tracking when simulation transfer state is reset. */
export function resetStocker2dDisplayRuntimeTracking(): void {
  trackedTransfers = extractTransfers(liveOhtRuntimeStore.getFrame());
  lastFrameNotifyMs = 0;
}

/** Clear inventory fingerprints when simulation state is reset. */
export function resetStocker2dDisplayInventoryTracking(): void {
  lastHeldInventoryFingerprint = stockerInventoryFingerprint(
    liveInventoryDisplayStore.getSnapshot()
  );
  liveStreamInventoryStore.resetFingerprint();
}

/**
 * Unified refresh subscription for 2D stocker dots:
 * - simulationFrameBus schedule (transfer progress, throttled during active handoffs)
 * - held + stream inventory fingerprints (hold release / stream column updates)
 */
export function subscribeStocker2dDisplayRefresh(onChange: () => void): () => void {
  refreshListeners.add(onChange);
  ensureFrameSubscription();
  ensureInventorySubscriptions();
  return () => {
    refreshListeners.delete(onChange);
    if (refreshListeners.size === 0) {
      if (frameUnsub) {
        frameUnsub();
        frameUnsub = null;
      }
      if (inventoryUnsub) {
        inventoryUnsub();
        inventoryUnsub = null;
      }
      if (streamInventoryUnsub) {
        streamInventoryUnsub();
        streamInventoryUnsub = null;
      }
      trackedTransfers.clear();
      lastFrameNotifyMs = 0;
    }
  };
}

/** @deprecated Use subscribeStocker2dDisplayRefresh */
export function subscribeStocker2dDisplayRuntime(onChange: () => void): () => void {
  return subscribeStocker2dDisplayRefresh(onChange);
}

/** @deprecated Use subscribeStocker2dDisplayRefresh */
export function subscribeStocker2dDisplayInventory(onChange: () => void): () => void {
  return subscribeStocker2dDisplayRefresh(onChange);
}
